/**
 * Sources whose channel is scoped to a job — mirrors the backend's job-scoped
 * lookup set (`generateChannelIDV2`). Everything else is never job-bound, which
 * is why a stray `jobId` on `candidate_search` has to be ignored rather than
 * sent: it would scope the lookup to a conversation no other surface can find.
 *
 * A wider set than `isJobBoundedSource` in `job-bounded.ts`, and deliberately:
 * that one answers what a channel is, this one answers how it is looked up.
 *
 * Channel ids are never composed here (AI-3133): only the backend knows which
 * person holds a conversation, so callers send members and keep the echoed id.
 */
const JOB_BOUND_SOURCES = new Set(['job_application', 'invite_to_apply', 'career_fair']);

/**
 * Whether this source is job-scoped — asked by the BFF's ownership guard and by
 * start-chat's lookup.
 *
 * Exported so the guard cannot name a subset of the set the id generator uses.
 * It once spelled `job_application` on its own, and the missing member was a
 * hole: `career_fair` plus a job id minted a channel carrying a job the caller
 * never had to own, and passed both validators on the way.
 */
export function isJobBoundSource(source: string): boolean {
  return JOB_BOUND_SOURCES.has(source);
}

/** The jobseeker's member id: `js_` + digits. Idempotent, so a host that already
 *  prefixed does not end up with `js_js_…`. */
export function memberIdFor(applicantId: string): string {
  return applicantId.startsWith('js_') ? applicantId : `js_${applicantId}`;
}

/** The jobseeker in a channel id (`…-js-<id>[-job-<job>]`), or undefined. */
export function jobseekerIdFromChannel(channelId: string): string | undefined {
  return /-js-(\d+)(?:-|$)/.exec(channelId)?.[1];
}

/**
 * The id behind a jobseeker or recruiter member (`js_` / `emp_`). Idempotent,
 * like `memberIdFor`.
 *
 * Exported because two worlds meet here: chat speaks `js_5195461`, and upstreams
 * that key on the AJT user row (calendar-service's `external_jobseeker_id`,
 * ajt2.5's `user_id`) speak integers. One stripper, so the two forms cannot
 * drift — `server/src/routes/conversations.ts` predates this and still inlines
 * its own; fold those in when next editing them.
 */
export function digitsOf(memberId: string): string {
  for (const prefix of ['js_', 'emp_']) {
    if (memberId.startsWith(prefix)) return memberId.slice(prefix.length);
  }
  return memberId;
}
